import { reactMount } from '@/lib/reactMount'
import { Chevron, ArrowRight, Delete, Search } from '@/components/Icons'
import { Fragment, useEffect, useRef, useState, useMemo } from 'react'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import MultiSelectDropdown from '@/components/MultiSelectField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ErrorMessage } from '@/components/ErrorMessage'
import useSWRMutation from 'swr/mutation'
import { fetcher } from '@/lib/fetcher'
import { gtmTrackEvent } from '@/lib/analytics'

const DealerCard = ({ dealer }) => {
  const websiteUrl = dealer?.Website && String(dealer.Website).trim() !== ''
    ? dealer.Website
    : null

  const optionalCtaUrl = (() => {
    if (!dealer?.OptionalCTA) return null
    if (typeof dealer.OptionalCTA === 'string') return dealer.OptionalCTA
    if (typeof dealer.OptionalCTA === 'object' && dealer.OptionalCTA.Url) return dealer.OptionalCTA.Url
    return null
  })()

  const optionalCtaText = (() => {
    if (!dealer?.OptionalCTA) return 'Apply for finance'
    if (typeof dealer.OptionalCTA === 'object' && dealer.OptionalCTA.Name) return dealer.OptionalCTA.Name
    return 'Apply for finance'
  })()

  const optionalCtaTarget = (() => {
    if (!dealer?.OptionalCTA) return null
    if (typeof dealer.OptionalCTA === 'object' && dealer.OptionalCTA.Target) return dealer.OptionalCTA.Target
    return null
  })()

  return (
    <article className='space-y-4 flex flex-col' data-theme='light'>
      <figure className='flex aspect-[250/42] max-w-[250px] relative'>
        {dealer?.Logo?.Url && (
          <img
            src={dealer?.Logo?.Url}
            alt={`${dealer?.Name || 'Dealer'} logo`}
            className='w-full h-full absolute inset-0 max-w-full max-h-full object-contain object-[left_center]'
          />
        )}
      </figure>

      <header className='space-y-1'>
        <h3 className='text-default-900 font-extrabold text-xl'>
          {dealer?.Name || 'Dealer'}
        </h3>
        <div data-spacer />
        {(dealer?.Address || dealer?.Region) && (
          <p className='text-base font-semibold text-default-600'>
            {/* Split address by line breaks to render multi-line addresses properly (e.g., "PO Box 123\nCity\nCountry") */}
            {(dealer?.Address || dealer?.Region).split('\n').map((line, index, array) => (
              <Fragment key={index}>
                {line}
                {index < array.length - 1 && <br />}
              </Fragment>
            ))}
          </p>
        )}
        <div data-spacer />
        {(dealer?.PhoneNumber) && (
          <a className='text-base font-extrabold text-default-600 [@media(hover:hover)]:hover:text-default-900 [@media(hover:hover)]:hover:underline' href={`tel:${dealer.MarketingPhoneNumber.replace(/\D/g, '')}`} target='_blank' rel='noopener noreferrer'>
            {dealer.MarketingPhoneNumber}
          </a>
        )}
      </header>

      <div className='flex flex-col gap-3 grow justify-end'>
        <div>
          <div className='w-full flex gap-3 grow items-center'>
            {websiteUrl && (
              <Button
                href={websiteUrl}
                target='_blank'
                variant='secondary'
                size='sm'
                className='!px-2 grow'
                onClick={() => gtmTrackEvent('clickButtonDealerWebsite', { link: 'View website', linkDestination: websiteUrl, dealerName: dealer.Name, pageURL: window.location.href })}
              >
                <span className='flex items-center gap-2'>
                  View website <ArrowRight className='size-4' />
                </span>
              </Button>
            )}
            {optionalCtaUrl && (
              <Button
                href={optionalCtaUrl}
                target={optionalCtaTarget || undefined} variant='link' size='sm' className='!px-2'
                onClick={() => gtmTrackEvent('clickButtonDealerOptionalCTA', { link: optionalCtaText, linkDestination: optionalCtaUrl, dealerName: dealer.Name, pageURL: window.location.href })}
              >
                <span className='flex items-center gap-2'>
                  {optionalCtaText} <ArrowRight className='size-4' />
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}

// Filter Section
const Filter = ({
  regionOptions,
  productOptions,
  brandOptions,
  conditionOptions,
  handleProductChange,
  selectedProduct,
  handleBrandChange,
  selectedBrand,
  handleConditionChange,
  selectedCondition,
  handleRegionChange,
  selectedRegion,
  handleClearAll
}) => {
  const shouldShowCondition = selectedProduct.some(product => productOptions.find(opt => opt.Name === product)?.Value === 'MV')
  const hasAnyFilters = selectedProduct.length > 0 || selectedBrand.length > 0 || selectedCondition !== '' || selectedRegion.length > 0

  return (
    <form className='mx-auto max-w-[1060px] px-4 xl:px-3 pb-14 bg-transparent'>
      <div className={`grid grid-cols-1 md:grid-cols-2 ${shouldShowCondition ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} gap-y-6 md:gap-3 flex-1`}>
        <MultiSelectDropdown
          type='select'
          label='Products'
          id='product'
          name='product'
          selectedValues={selectedProduct}
          options={productOptions.map(option => option.Name)}
          onChange={handleProductChange}
          placeholder='Select products'
        />
        <MultiSelectDropdown
          type='select'
          label='Brand'
          id='brand'
          name='brand'
          selectedValues={selectedBrand}
          options={brandOptions.map(option => option.Name)}
          placeholder='Select brand'
          onChange={handleBrandChange}
        />
        {shouldShowCondition && (
          <MultiSelectDropdown
            type='select'
            label='Condition'
            id='condition'
            name='condition'
            selectedValues={selectedCondition}
            options={conditionOptions}
            onChange={handleConditionChange}
            placeholder='Select condition'
          />
        )}
        <MultiSelectDropdown
          type='select'
          label='Region'
          id='region'
          name='region'
          selectedValues={selectedRegion}
          options={regionOptions}
          onChange={handleRegionChange}
          placeholder='Select region'
        />
      </div>
    </form>
  )
}

export const FindDealerPage = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current

  const [selectedProduct, setSelectedProduct] = useState([])
  const [selectedBrand, setSelectedBrand] = useState([])
  const [selectedCondition, setSelectedCondition] = useState([])
  const [selectedRegion, setSelectedRegion] = useState([])
  const hasInitialisedSelectedProductFromUrl = useRef(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef(null)
  const {
    trigger: triggerSearch,
    data: dealerBuyingOptionsResults,
    error,
    isMutating: isLoading
  } = useSWRMutation('/api/data/DealerBuyingOptions', fetcher)

  useEffect(() => {
    triggerSearch({ method: 'GET' })
  }, [triggerSearch])

  const productOptions = useMemo(() =>
    (dealerBuyingOptionsResults?.data || []).slice().sort((a, b) =>
      String(a?.Name || '').localeCompare(String(b?.Name || ''), undefined, { sensitivity: 'base' })
    ),
  [dealerBuyingOptionsResults?.data]
  )
  const brandOptions = useMemo(() =>
    (umbraco.Brands || []).slice().sort((a, b) =>
      String(a?.Name || '').localeCompare(String(b?.Name || ''), undefined, { sensitivity: 'base' })
    ),
  [umbraco.Brands]
  )
  const conditionOptions = ['New', 'Used']

  // Initialize selectedProduct from URL query param `product` when options are available
  useEffect(() => {
    if (hasInitialisedSelectedProductFromUrl.current) return
    const params = new URLSearchParams(window.location.search)
    const productParam = params.get('product')
    if (!productParam || !Array.isArray(productOptions) || productOptions.length === 0) return

    const matched = productOptions.find(opt => String(opt.Value) === String(productParam) || String(opt.Name) === String(productParam))
    if (matched) {
      setSelectedProduct([matched.Name])
      hasInitialisedSelectedProductFromUrl.current = true
    }
  }, [productOptions])

  const regionOptions = useMemo(() => {
    const regions = (umbraco.Dealers || [])
      .map(dealer => dealer.Region)
      .filter(region => region && region.trim() !== '')

    // Deduplicate case-insensitively while preserving first-seen casing
    const seen = new Set()
    const uniqueRegions = []
    for (const region of regions) {
      const key = String(region).toLocaleLowerCase()
      if (!seen.has(key)) {
        seen.add(key)
        uniqueRegions.push(region)
      }
    }

    // Case-insensitive sort so "east coast" appears with "E"
    return uniqueRegions.sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
    )
  }, [umbraco.Dealers])

  const handleProductChange = (values) => {
    setSelectedProduct(values)
    const hasMVProduct = values.some(product => productOptions.find(opt => opt.Name === product)?.Value === 'MV')
    if (!hasMVProduct) {
      setSelectedCondition([])
    }
    gtmTrackEvent('dealerFilterProductChange', { product: values, pageURL: window.location.href })
  }

  const handleBrandChange = (values) => {
    setSelectedBrand(values)
    gtmTrackEvent('dealerFilterBrandChange', { brand: values, pageURL: window.location.href })
  }

  const handleConditionChange = (values) => {
    setSelectedCondition(values)
    gtmTrackEvent('dealerFilterConditionChange', { condition: values, pageURL: window.location.href })
  }

  const handleRegionChange = (values) => {
    setSelectedRegion(values)
    gtmTrackEvent('dealerFilterRegionChange', { region: values, pageURL: window.location.href })
  }

  const handleClearAll = () => {
    setSelectedProduct([])
    setSelectedBrand([])
    setSelectedCondition([])
    setSelectedRegion([])
    gtmTrackEvent('dealerFilterClearAll', { pageURL: window.location.href })
  }

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
    gtmTrackEvent('dealerSearchQuery', { query: e.target.value, pageURL: window.location.href })
  }

  const handleSearchClear = () => {
    setSearchQuery('')
    searchInputRef.current?.focus()
  }

  const dealers = umbraco.Dealers || []
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(12)

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value)
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedProduct, selectedBrand, selectedCondition, selectedRegion, searchQuery])

  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      }
    }

    return pages
  }

  const filteredDealers = useMemo(() => {
    const MarketVisibleDealers = dealers.filter(dealer => dealer.Hide !== true)
    const SearchDealer = searchQuery.trim().toLowerCase()

    const filteredDealers = MarketVisibleDealers.filter(dealer => {
      // Product filter => dealer.BuyingOptions contains any selected product code
      if (selectedProduct.length > 0) {
        const dealerOptions = Array.isArray(dealer?.BuyingOptions) ? dealer.BuyingOptions : []
        const hasMatchingProduct = selectedProduct.some(productName => {
          const productCode = productOptions.find(opt => opt.Name === productName)?.Value
          return productCode && dealerOptions.includes(String(productCode))
        })
        if (!hasMatchingProduct) return false
      }

      // Brand filter => dealer.Brand equals brandCode or selectedBrand (case-insensitive)
      if (selectedBrand.length > 0) {
        const dealerBrand = String(dealer?.Brand || '').toLowerCase().trim()
        const additionalBrands = Array.isArray(dealer?.AdditionalBrands)
          ? dealer.AdditionalBrands.map(brand => String(brand || '').toLowerCase().trim())
          : []

        const matchBrand = selectedBrand.some(brandName => {
          const normalizedBrandName = String(brandName || '').toLowerCase().trim()
          const brandCode = brandOptions.find(opt => String(opt.Name || '').toLowerCase().trim() === normalizedBrandName)?.Value
          const normalizedBrandCode = brandCode ? String(brandCode).toLowerCase().trim() : ''

          return (
            dealerBrand === normalizedBrandName ||
            (normalizedBrandCode && dealerBrand === normalizedBrandCode) ||
            additionalBrands.includes(normalizedBrandName) ||
            (normalizedBrandCode && additionalBrands.includes(normalizedBrandCode))
          )
        })
        if (!matchBrand) return false
      }

      // Condition filter (applies typically for MV)
      if (selectedCondition.length > 0) {
        const conditions = Array.isArray(dealer?.CarConditions) ? dealer.CarConditions : []

        const MatchingCondition = selectedCondition.some(cond =>
          conditions.includes(cond.toLowerCase())
        )
        if (!MatchingCondition) return false
      }

      // Region filter
      if (selectedRegion.length > 0) {
        if (!selectedRegion.includes(dealer?.Region)) return false
      }

      if (SearchDealer) {
        const searchableFields = [
          dealer?.Name,
          dealer?.Address,
          dealer?.Brand,
          dealer?.Region
        ]
        const searchableText = searchableFields
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        if (!searchableText.includes(SearchDealer)) return false
      }

      return true
    })
      .sort((a, b) => String(a?.Name || '').localeCompare(String(b?.Name || '')))

    return filteredDealers
  }, [dealers, productOptions, brandOptions, selectedProduct, selectedBrand, selectedCondition, selectedRegion, searchQuery])

  const totalItems = filteredDealers.length
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  const currentDealers = filteredDealers.slice(startIndex, endIndex)
  function formatListWithAnd (arr) {
    if (!arr || arr.length === 0) return null

    const items = arr.map((item, index) => (
      <strong key={index} className='font-black text-default-900'>
        "{item}"
      </strong>
    ))

    if (items.length === 1) return items[0]

    if (items.length === 2) {
      return <>{items[0]} and {items[1]}</>
    }

    return (
      <>
        {items.slice(0, -1).map((item, i) => (
          <span key={i}>{item}, </span>
        ))}
        and {items[items.length - 1]}
      </>
    )
  }

  return (
    <>
      <section className='relative isolate pt-48 pb-24 md:px-4 lg:px-10 xl:px-24'>
        <header className='px-4 md:px-0 max-w-3xl text-center mx-auto mb-20'>
          {umbraco.Title && (
            <h1 className='text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900'>
              {umbraco.Title}
            </h1>
          )}
          {umbraco.Summary && (
            <div className='_simple-rte mt-4 text-base text-pretty font-semibold' dangerouslySetInnerHTML={{ __html: umbraco.Summary }} />
          )}
          <div className='mt-16 flex items-center gap-3 max-w-[400px] mx-auto'>
            <div className='relative grow'>
              {!searchQuery && (<span className='absolute right-3 top-1/2 -translate-y-1/2 text-default-900 pointer-events-none'>
                <Search className='size-5' />
              </span>)}
              <input
                ref={searchInputRef}
                type='text'
                id='dealerSearch'
                name='dealerSearch'
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder='Search for a dealer'
                aria-label='Search for a dealer'
                className='w-full h-11 rounded-lg border border-default-200 focus:border-default-400 pl-4 pr-10 font-semibold text-base text-default-700 bg-default-50 shadow-sm outline-none transition placeholder:text-base placeholder:text-default-400'
              />
              {searchQuery && (
                <button
                  type='button'
                  onClick={handleSearchClear}
                  aria-label='Clear search'
                  className='absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center size-[36px] rounded-full hover:bg-primary-100 active:bg-primary-100'
                >
                  <Delete className='size-4' />
                  <span className='sr-only'>Clear</span>
                </button>
              )}

            </div>
          </div>
          {!isLoading && searchQuery.trim() && (
            <div className='mt-4 text-center text-base font-semibold text-default-700'>
              {filteredDealers.length} search result{filteredDealers.length !== 1 && 's'} for
              {' '}
              <span className='font-black text-default-900'>"{searchQuery.trim()}"</span>
              {(() => {
                const filters = [
                  ...selectedProduct,
                  ...selectedBrand,
                  ...selectedCondition,
                  ...selectedRegion
                ].filter(Boolean)
                if (filters.length === 0) return null
                return (
                  <>
                    {', '}
                    {formatListWithAnd(filters)}
                  </>
                )
              })()}
            </div>
          )}
        </header>

        <Filter
          productOptions={productOptions}
          brandOptions={brandOptions}
          conditionOptions={conditionOptions}
          regionOptions={regionOptions}
          handleProductChange={handleProductChange}
          selectedProduct={selectedProduct}
          handleBrandChange={handleBrandChange}
          selectedBrand={selectedBrand}
          handleConditionChange={handleConditionChange}
          selectedCondition={selectedCondition}
          handleRegionChange={handleRegionChange}
          selectedRegion={selectedRegion}
          handleClearAll={handleClearAll}
        />

        <div className='mx-auto max-w-[1060px] px-4 xl:px-3 space-y-16'>
          <div className='mx-auto space-y-8'>
            {isLoading && <LoadingSpinner message='Loading dealers...' />}

            {!isLoading && currentDealers.length > 0 && (
              <div className='grid grid-cols-1 gap-x-8 gap-y-10 md:gap-y-16 sm:grid-cols-2 lg:grid-cols-3'>
                {currentDealers.map((dealer, idx) => (
                  <DealerCard key={`${dealer.Name}-${idx}`} dealer={dealer} />
                ))}
              </div>
            )}

            {!isLoading && !error && filteredDealers.length === 0 && dealers.length > 0 && (
              <div className='text-center '>
                <p className='text-gray-500 text-lg'>
                  No Dealers found, please update filters and try again.
                </p>
              </div>
            )}

            {!isLoading && error && (
              <ErrorMessage message='Failed to load dealers. Please refresh the page.' />
            )}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <nav
              aria-label='Pagination'
              className='w-full'
            >
              <div className='hidden lg:flex flex-row items-center justify-between w-full'>
                <p className='text-default-500 font-semibold text-sm max-lg:grow text-center shrink-0'>
                  Page {currentPage} of{' '}
                  {totalPages}
                </p>

                <div className='flex flex-row gap-1 grow justify-end'>

                  {/* Previous button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-1 pl-4 pr-5 pt-[7px] pb-[9px] rounded border font-extrabold text-sm shadow-sm transition duration-300 ${currentPage === 1
                                            ? 'text-default-300 border-default-300 pointer-events-none'
                                            : 'border-default-900 text-default-900'
                                            }`}
                  >
                    <ArrowRight className='rotate-180 size-6' />
                    <span>Previous</span>
                  </button>

                  {/* Page numbers */}
                  {getPageNumbers().map((page, index) => (
                    <Fragment key={index}>
                      {page === '...' && (
                        <span className='flex justify-center items-center pb-2.5 bg-default-50 border border-default-200 text-default-900 text-center font-extrabold text-sm rounded px-1 py-2 w-11 shadow-sm'>
                          ...
                        </span>
                      )}
                      {page !== '...' && (
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`flex justify-center items-center pb-2.5 font-extrabold text-sm rounded px-1 py-2 w-11 shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${page === currentPage
                                                        ? 'z-10 text-default-50 bg-primary-950 border border-primary-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-950'
                                                        : 'bg-default-50 border border-default-200 text-default-900'
                                                        }`}
                        >
                          {page}
                        </button>
                      )}
                    </Fragment>
                  ))}

                  {/* Next button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border border-default-900 text-default-900 font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === totalPages
                                            ? 'text-default-300 border-default-300 pointer-events-none'
                                            : 'border-default-900 text-default-900'
                                            }`}
                  >
                    <span>Next</span>
                    <ArrowRight className='size-6' />
                  </button>

                </div>
                {/* Items per page selector */}
                {totalItems >= 12 && (
                  <label className='relative ml-12'>
                    <select
                      id='itemsPerPage'
                      name='itemsPerPage'
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className='cursor-pointer bg-default-50 h-11 w-full rounded-lg border border-default-200 focus:border-default-400 pb-px pr-10 pl-3 font-semibold text-[14px] text-default-700 appearance-none shadow-sm'
                    >
                      <option value={12}>12 / page</option>
                      <option value={24}>24 / page</option>
                      <option value={48}>48 / page</option>
                    </select>
                    <Chevron className='text-default-700 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                  </label>
                )}
              </div>

              {/* Mobile pagination */}
              <div className='flex flex-row items-center justify-between w-full lg:hidden'>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === 1
                                        ? 'text-default-300 border-default-300 pointer-events-none'
                                        : 'border-default-900 text-default-900'
                                        }`}
                >
                  <span className='sr-only'>previous</span>
                  <ArrowRight className='rotate-180 size-6' />
                </button>
                <p className='text-default-500 font-semibold text-sm grow text-center'>
                  Page {currentPage} of{' '}
                  {totalPages}
                </p>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border border-default-900 text-default-900 font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === totalPages
                                        ? 'text-default-300 border-default-300 pointer-events-none'
                                        : 'border-default-900 text-default-900'
                                        }`}
                >
                  <span className='sr-only'>Next</span>
                  <ArrowRight className='size-6' />
                </button>
              </div>
            </nav>
          )}
        </div>
      </section>
    </>
  )
}

reactMount('FindDealerPage', FindDealerPage)
